import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getFormValues } from "redux-form";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import order from "../../order";
import cart from "../../cart";
import { getUserContext } from "../../userContext/userContext.selectors";
import { getDefaultSku } from "../../order/order.selectors";
import { getOrderIdFromUrl } from "modules/utilities/url";
import { getPaymentInfo } from "../../cart/cart.selectors";
import {
  startPaymentAttempt,
  setPaymentPhase,
  setRedirect,
  toggleDialog,
  getPaymentStatus,
} from "../paymentModal.actions";
import {
  getPaymentPhase,
  getPaymentAttempt,
  getPaymentAttemptId,
  getPaymentError,
  getPaymentCanRetry,
} from "../paymentModal.selectors";
import { FORM_NAME } from "../paymentModal.constants";
import { usePaymentAttemptPoll } from "../usePaymentAttemptPoll";
import CheckoutForm from "./CheckoutForm";
import controls from "modules/controls";

const {
  selectors: { getOrderHOF },
} = order;

const {
  selectors: { getPromoValue, getSku },
} = cart;

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(window.i2290settings.stripePublicKey);
  }
  return stripePromise;
};

const {
  components: {
    Button,
  },
} = controls;

/**
 * Stripe boundary orchestrator for STRIPE_STEP.
 * Uses deferred-intent mode: <Elements> mounts immediately with { mode, amount, currency }
 * so the PaymentElement renders without a clientSecret. /start is deferred to submit-time
 * inside CheckoutForm, allowing the user to freely edit the promo code before submitting.
 * Once /start resolves (clientSecret in Redux), CheckoutForm calls stripe.confirmPayment.
 */
const StripeCheckoutStep = ({
  orderId,
  sku,
  promoCode,
  ccHolderState,
  amountInCents,
  phase,
  attempt,
  attemptId,
  paymentError,
  canRetry,
  dispatchStartPaymentAttempt,
  dispatchSetPaymentPhase,
  dispatchToggleDialog,
  dispatchSetRedirect,
  dispatchGetPaymentStatus,
}) => {
  // Terminal success → close modal + navigate to success page
  useEffect(() => {
    console.log("[StripeCheckoutStep] phase changed →", phase);
    if (phase === "succeeded") {
      console.log("[StripeCheckoutStep] succeeded → closing modal + redirecting to /success");
      dispatchToggleDialog();
      dispatchSetRedirect("/success?orderId=" + orderId);
    }
  }, [phase, dispatchToggleDialog, dispatchSetRedirect, orderId]);

  // Activate polling hook — hook itself stops when phase is terminal, times out, or attemptId is null
  usePaymentAttemptPoll(phase === "polling" ? attemptId : null, phase, dispatchGetPaymentStatus, 
  dispatchSetPaymentPhase);

  const handleRetry = () => {
    dispatchSetPaymentPhase("idle");
  };

  const handleCancel = () => {
    dispatchToggleDialog();
  };

  // Hard failure (non-retryable)
  if (phase === "failed" && !canRetry) {
    const message =
      paymentError && paymentError.message
        ? paymentError.message
        : "Your payment could not be processed.";
    return (
      <div className="payment-modal-body">
        <div className="mb-gutter">
          <span className="i2290-inline-error">{message}</span>
        </div>
        <div className="row mt-gutter">
          <div className="col">
            <Button
                primary={true}
                disabled={false}
                text="Cancel"
                onClick={handleCancel}
                width="100%">
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Retryable failure
  if (phase === "failed" && canRetry) {
    const message =
      paymentError && paymentError.message
        ? paymentError.message
        : "Payment failed. You may try again.";
    return (
      <div className="payment-modal-body">
        <div className="mb-gutter">
          <span className="i2290-inline-error">{message}</span>
        </div>
        <div className="row mt-gutter">
          <div className="col clean-gutter-right">
            <Button
                primary={false}
                disabled={false}
                text="Cancel"
                onClick={handleCancel}
                width="100%">
            </Button>
          </div>
          <div className="col clean-gutter-left">
            <Button
                primary={true}
                disabled={false}
                text="Try Again"
                onClick={handleRetry}
                width="100%">
            </Button>
            
          </div>
        </div>
      </div>
    );
  }

  // Deferred-intent mode: mount Elements with mode+amount so PaymentElement renders
  // immediately. /start fires at submit-time inside CheckoutForm.
  return (
    <Elements
      stripe={getStripe()}
      options={{ 
        mode: "payment", 
        amount: amountInCents, 
        currency: "usd" 
      }}
    >
      <CheckoutForm
        orderId={orderId}
        sku={sku}
        promoCode={promoCode}
        cancelPayment={handleCancel}
        ccHolderState={ccHolderState}
        amountInCents={amountInCents}
        dispatchStartPaymentAttempt={dispatchStartPaymentAttempt}
      />
    </Elements>
  );
};

StripeCheckoutStep.propTypes = {
  orderId: PropTypes.number,
  sku: PropTypes.string,
  promoCode: PropTypes.string,
  ccHolderState: PropTypes.string,
  amountInCents: PropTypes.number.isRequired,
  phase: PropTypes.string.isRequired,
  attempt: PropTypes.object,
  attemptId: PropTypes.number,
  paymentError: PropTypes.shape({ kind: PropTypes.string, message: PropTypes.string }),
  canRetry: PropTypes.bool,
  dispatchStartPaymentAttempt: PropTypes.func.isRequired,
  dispatchSetPaymentPhase: PropTypes.func.isRequired,
  dispatchToggleDialog: PropTypes.func.isRequired,
  dispatchSetRedirect: PropTypes.func.isRequired,
  dispatchGetPaymentStatus: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const userContext = getUserContext(state);
  const orderId = getOrderIdFromUrl();
  const orderObj = getOrderHOF(orderId)(state);
  const defaultSku =
    orderObj !== undefined
      ? getDefaultSku(orderObj.Id, userContext && userContext.Unlimited)(state)
      : undefined;
  const promoValue = getPromoValue(state);
  const formValues = getFormValues(FORM_NAME)(state) || {};
  const paymentInfo = getPaymentInfo(state);
  // Stripe requires amount in smallest currency unit (cents). total is in dollars.
  const amountInCents = paymentInfo && paymentInfo.total
    ? Math.round(paymentInfo.total * 100)
    : 100;

  return {
    orderId,
    sku: getSku(defaultSku)(state),
    promoCode: promoValue ? promoValue.code : undefined,
    ccHolderState: formValues?.CcHolderState || null,
    amountInCents,
    phase: getPaymentPhase(state),
    attempt: getPaymentAttempt(state),
    attemptId: getPaymentAttemptId(state),
    paymentError: getPaymentError(state),
    canRetry: getPaymentCanRetry(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatchStartPaymentAttempt: (orderId, sku, promoCode, ccBillingFields) =>
    dispatch(startPaymentAttempt(orderId, sku, promoCode, ccBillingFields)),
  dispatchSetPaymentPhase: (phase, error) =>
    dispatch(setPaymentPhase(phase, error)),
  dispatchToggleDialog: () => dispatch(toggleDialog()),
  dispatchSetRedirect: (path) => dispatch(setRedirect(path)),
  dispatchGetPaymentStatus: (attemptId) => dispatch(getPaymentStatus(attemptId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StripeCheckoutStep);